import { CdkPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { debounceTime, forkJoin, map } from 'rxjs';
import { EForm, EFormRegistry, EFormView, EFormViewRegistry, FormValidationErrorController, SectionMetadata } from '../../../common-shared-module/model/eform/eform-api.model';
import { ValidationSectionBase } from '../../../common-shared-module/model/eform/scripts/validation-section.model';
import { ProcedureInstanceBase } from '../../../common-shared-module/model/procedure-instance/procedure-instance.model';
import { ConfigService } from '../../../common-shared-module/services/config/config.service';
import { EFormFactoryService } from '../../../common-shared-module/services/eform/e-form-factory.service';
import { EFormViewFactoryService } from '../../../common-shared-module/services/eform/e-form-view-factory.service';
import { EncapsulateViewComponent } from "../../components/encapsulate-view/encapsulate-view.component";
import { PageScrollContentComponent } from "../../../common-shared-module/components/page-scroll-content/page-scroll-content.component";
import { ProcedureInstanceVisibilityService } from '../../../common-shared-module/services/procedure-instance/procedure-instance-visibility.service';
import { EFormValueService } from '../../../common-shared-module/services/eform/e-form-value.service';
import { EFormDynFormService } from '../../../common-shared-module/services/eform/e-form-dyn-form.service';
import { EFormDynamicSectionMetadataService } from '../../../common-shared-module/services/eform/e-form-dynamic-section-metadata.service';
import { EFormPrintService } from '../../../common-shared-module/services/eform/e-form-print.service';
import { PrintProcedureInstanceInfoBase } from '../../../common-shared-module/model/print/print-procedure-instance-info.model';
import { ProcedureInstanceConfigCacheService } from '../../../common-shared-module/services/procedure-instance/procedure-instance-config-cache.service';
import { FormPreviewComponent } from '../../../common-shared-module/components/form-preview/form-preview.component';
import { MatDialog } from '@angular/material/dialog';

export enum FormType {
    View = 0,
    Fill = 1,
    Print = 2,
    Preview = 3
}

@Component({
    selector: 'app-form-viewer',
    templateUrl: './form-viewer.component.html',
    styleUrl: './form-viewer.component.scss',
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: true,
    imports: [
    CommonModule,
    CdkPortalOutlet,
    EncapsulateViewComponent,
    PageScrollContentComponent,
    FormPreviewComponent
]
})
export class FormViewerComponent implements EFormRegistry, EFormViewRegistry, FormValidationErrorController {

    // Method for FormType.View //
    @Output() getSectionIndex = new EventEmitter<any>()

    // Methods for FormType.Fill //
    @Output() getLastModified = new EventEmitter<any>()
    @Output() getSavingStatus = new EventEmitter<any>()
    @Output() getErrorSavingCode = new EventEmitter<any>()
    @Output() setValidationFormErrors = new EventEmitter<any>()
    @Output() doClearErrors = new EventEmitter<any>()
    @Output() doPutSectionValidationErrors = new EventEmitter<any>()

    @Input() validationSection!: ValidationSectionBase []

    // Methods for FormType.Print //
    @Output() isLoadedForm = new EventEmitter<any>()
    @Output() getPrintProcedureInstanceInfo = new EventEmitter<any>()

    //Methods for FormType.Preview //
    @Output() getReady = new EventEmitter<any>()

    // Generic Method for all FormType //
    @Input()
    set init(
        opts: {
            type: number, 
            pi: { 
                id: number, procedureFormId: number, serviceInstanceId: number, procedureInstanceCode: number, 
                serviceInstanceCode?: number, state: number 
            },
            secret?: string
        }
    ) {
        
        if (opts == undefined || opts.type == undefined || opts.pi == undefined) {
            console.log("ERROR NO PARAMS FOUND")
            return
        }

        if (this.isValidType(opts.type)) {
            this._type = opts.type
        } else {
            // TODO - Pantalla de error //
            this.getError.emit()
            return
        }

        if (this._type == FormType.Preview) {
            if (opts.pi.procedureFormId == undefined) {
                console.log("ERROR PROCEDUREFORMID NOT FOUND")
                this.getError.emit()
                return
            }

            this.configService.init().subscribe(() => {
                let newPI = new ProcedureInstanceBase()
                newPI.procedureFormId = opts.pi.procedureFormId
                this._pi = newPI
                this.changeDetectorRef.detectChanges()
            })

            return
        }

        if (!opts.secret) {

            if (opts.pi.id == undefined || opts.pi.procedureFormId == undefined || opts.pi.serviceInstanceId == undefined || 
                opts.pi.state == undefined || opts.pi.procedureInstanceCode == undefined) {
                console.log("ERROR NO PI PARAMS FOUND")
                // TODO - Pantalla de error //
                this.getError.emit()
                return
            }

        }

        let newPI = new ProcedureInstanceBase()
        newPI.id = opts.pi.id
        newPI.procedureFormId = opts.pi.procedureFormId
        newPI.serviceInstanceId = opts.pi.serviceInstanceId
        newPI.state = opts.pi.state
        newPI.procedureInstanceCode = opts.pi.procedureInstanceCode

        if (opts.pi.serviceInstanceCode) {
            newPI.serviceInstanceCode = opts.pi.serviceInstanceCode
        }

        this._pi = newPI

        if (this._type == FormType.Print) {

            if (opts.pi.procedureInstanceCode == undefined || opts.pi.serviceInstanceCode == undefined) {
                console.log('ERRRO NO PI OR SI PARAMS FOUND')
                // TODO - Pantalla de error //
                this.getError.emit()
                return
            }

            if (opts.secret) {
                this._secret = opts.secret
                this.refreshFormPrint()
                return
            }

        }

        // Check if login exists and is not expired //
        this.configService.init().subscribe(() => {
            this.refreshForm()
        })
    }
    _type!: FormType
    _pi!: ProcedureInstanceBase
    _secret: string | null = null
    _secretLoaded: boolean = false

    @Output() getError = new EventEmitter<any>()

    // Use this for custom EForm and EFormSection //
    procedureCode: string = ""
    serviceCode: string = ""

    theForm: ComponentPortal<EForm> | null = null
    theFormView: ComponentPortal<EFormView> | null = null

    registeredEForm: EForm | null = null
    registeredEFormView: EFormView | null = null

    //This method is needed to implement the FormValidationErrorController and detect changes into the form errors 
    set sectionErrors(s: ValidationSectionBase[]) {
        this.setValidationFormErrors.emit(s)
    }

    //This method is needed to implement the FormValidationErrorController and detect changes into the form errors 
    get sectionErrors(): ValidationSectionBase[] {
        return this.validationSection
    }

    constructor(
        protected configService: ConfigService,
        protected eFormFactoryService: EFormFactoryService, protected eFormViewFactoryService: EFormViewFactoryService,
        protected eFormValueService: EFormValueService, protected eFormDynFormService: EFormDynFormService,
        protected eFormDynamicSectionMetadataService: EFormDynamicSectionMetadataService,
        protected eFormPrintService: EFormPrintService,
        protected procedureInstanceVisibilityService: ProcedureInstanceVisibilityService,
        protected procedureInstanceConfigCacheService: ProcedureInstanceConfigCacheService,
        protected dialog: MatDialog,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) { }

    secretInitialize() {
        return this._secret != null
    }

    register(form: EForm): void {
        this.registeredEForm = form
        this.registeredEForm.isSaving.pipe(map(l => {
            if (l) {
                setTimeout(() => this.getLastModified.emit(new Date()), 100)
            }
            return l
        }), debounceTime(1500)).subscribe(c => {
            this.getSavingStatus.emit(c)
        })

        this.registeredEForm.saveResult.subscribe(r => {
            if (r.ok) {
                this.getErrorSavingCode.emit(null)
                return
            }
            this.getErrorSavingCode.emit(r.messageCode)
        })
    }

    unRegister(form: EForm): void {
        this.registeredEForm = null
    }

    registerView(form: EFormView): void {
        this.registeredEFormView = form
        this.registeredEFormView.sections().subscribe(s => {
            this.getSectionIndex.emit(s)
        })
    }

    isAllSectionsLoaded(): void {
        this.isLoadedForm.emit(this._secret == null)
    }

    unRegisterView(form: EFormView): void {
        this.registeredEFormView = null
    }

    clearErrors(sectionId: number): void {
        this.doClearErrors.emit(sectionId)
    }

    putSectionValidationErrors(validationSection: ValidationSectionBase): void {
        this.doPutSectionValidationErrors.emit(validationSection)
    }


    refreshForm = (): void => {

        if (this._type == FormType.Fill) {
            this.refreshFormFill()
            return
        }

        if (this._type == FormType.Print) {
            this.refreshFormPrint()
        }

        if (this._type == FormType.View) {
            this.refreshFormView()
        }

    }

    refreshFormFill(): void {

        this.eFormFactoryService.newEForm(this, this._pi, this, this.serviceCode, this.procedureCode).subscribe(r => {
            this.theForm = r
            this.changeDetectorRef.detectChanges()
        })

    }

    refreshFormView(): void {

        this.eFormViewFactoryService.newEFormView(this, this._pi, this.serviceCode, this.procedureCode).subscribe(r => {
            this.theFormView = r
            this.changeDetectorRef.detectChanges()
        })
    }

    refreshFormPrint(): void {

        if (this._secret) {
            this.loadDataFromSecret()
            return
        }
        
        this.eFormViewFactoryService.newEFormView(this, this._pi, this.serviceCode, this.procedureCode).subscribe(r => {
            this.theFormView = r
            this.changeDetectorRef.detectChanges()
        })
    }

    private loadDataFromSecret() {
		this.eFormPrintService.findFullProcedureInstanceInfo(this._pi.serviceInstanceCode, this._pi.procedureInstanceCode, this._secret!).subscribe(p => {
			if (p.messageCode != null || p.procedureInstanceTOs.length == 0 || p.eformHeaderModelValue.ok == false) {
				this.getError.emit()
				return;
			}

			try {
				//set the values on the cache before to call
				this.preloadCaches(p)
			} catch (e) {
				this.getError.emit()
				return;
			}

            this._pi = p.procedureInstanceTOs[0]
            this.getPrintProcedureInstanceInfo.emit(p)

			this.eFormViewFactoryService.newEFormView(this, this._pi, this.serviceCode, this.procedureCode).subscribe(r => {
				this.theFormView = r;
				this.changeDetectorRef.detectChanges()
                setTimeout(() => { 
                    this._secretLoaded = true
                    this.changeDetectorRef.detectChanges()
                }, 100)
			});
		})
	}

    preloadCaches(info: PrintProcedureInstanceInfoBase) {
		if (info.eformHeaderModelValue.entity == null) {
			return
		}

		this.eFormValueService.preloadEFormHeaderModelValueCache(info.procedureInstanceTOs[0].serviceInstanceId, info.procedureInstanceTOs[0].id, info.eformHeaderModelValue)
		this.procedureInstanceConfigCacheService.preloadServicesConfigCache(info.procedureInstanceTOs[0].serviceId, info.procedureInstanceTOs[0].procedureFormId, info.config);
		this.eFormDynFormService.preloadEFormDynFormService(info.procedureInstanceTOs[0].procedureFormId, info.dynForms[0])

		let sections: SectionMetadata[] = info.eformHeaderModelValue.entity.sections
		for (let index = 0; index < sections.length; index++) {
			const element = sections[index];

			info.jsonSectionForm[index]["onFormCreation"] = false

			this.eFormDynamicSectionMetadataService.preloadSectionLayoutMetadata(element.sectionId, info.jsonSectionForm[index])
			this.eFormValueService.preloadFormSectionModelValueResponseCache(info.procedureInstanceTOs[0].serviceInstanceId, info.procedureInstanceTOs[0].id, element.sectionId, info.eformSectionModelValue[index])
		}
	}

    isReady() {
        this.getReady.emit()
    }

    isValidType(type: number) {
        return (type == FormType.Fill || type == FormType.View || type == FormType.Print || type == FormType.Preview)
    }

}
